import AddressServices from "../../../../services/AddressServices";

import { isValidDate } from "../../../../utils/time";
import { calcAge } from "../../../../utils/utility";
import { simulationColumnNames, simulationInsuredFields } from "./salud-pymes-insured-helpers";
import { Coverage, InsuredData, SimulationJsonData } from "./salud-pymes-insured-types";

export const validateAgeInsureds = (
  birthDate: string,
  coverages: Coverage[],
  serverError: boolean
): { error: boolean } => {
  const covReqAgeMax = coverages.filter((cov) => {
    if (cov.required && cov.edadMax !== "") return true;
    if (cov.groupRequired && cov.edadMax !== "") return true;
    return false;
  });

  const age = calcAge(birthDate) || 0;
  const isHighAge = covReqAgeMax.some((cov) => age > Number(cov.edadMax));

  if (isHighAge && !serverError) {
    return { error: true };
  }
  return { error: false };
};

export const validateBirthDate = (birthDate: string, coverages: Coverage[], rowNumber: number) => {
  const errors = [];
  if (!isValidDate(birthDate)) {
    errors.push(`Fecha de nacimiento inválida ${birthDate} (fila ${rowNumber})`);
  }
  const { error: ageError } = validateAgeInsureds(birthDate, coverages, false);
  if (ageError) {
    errors.push(
      `El asegurado con fecha de nacimiento ${birthDate} excede de la edad máxima permitida para alguna de las coberturas requeridas (fila ${rowNumber}).`
    );
  }
  return errors;
};

export const validatePostalCode = async (postalCode: string) => {
  const errors = [];
  if (!postalCode) {
    errors.push(`El código postal es obligatorio.`)
    return errors;
  }
  if (postalCode.length !== 5) {
    errors.push(`Código postal inválido: "${postalCode}".`);
    return errors;
  }
  const resPostalCodes = await AddressServices.getPostalCodes({
    loader: false,
    setLoading: () => {},
    params: [`input=${postalCode}`, `provinceCode=*`, `cityCode=*`, `street=0`],
  });

  if (resPostalCodes.length === 0) {
    errors.push(`Código postal inválido: "${postalCode}".`);
  }

  return errors;
};

const getValidCapitalValues = (coverage: Coverage): string[] => {
  return coverage && coverage.capitales ? coverage.capitales.map((capital) => capital.importe) : [];
};

export const validateSimulationInsureds = async ({
  jsonData,
  maxInsureds,
  coverages,
}: {
  jsonData: SimulationJsonData[];
  maxInsureds: number;
  coverages: Coverage[];
}) => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const newInsuredsLength = jsonData && jsonData.length ? jsonData.length : 0;
  const birthDates: string[] = [];
  const postalCodeList: Set<string> = new Set();

  if (newInsuredsLength < 1) {
    errors.push("La hoja de cálculo está vacía");
    return { errors, newInsureds: [] };
  }

  if (newInsuredsLength > maxInsureds) {
    errors.push(`La suma de asegurados es mayor a la permitida de ${maxInsureds} asegurados`);
    return { errors, newInsureds: [] };
  }

  const newInsureds: InsuredData[] = [];
  const mandatoryCoverages = coverages.filter(
    (cov: Coverage) => cov.code !== "GS30" && cov.code !== "GS09" && cov.code !== "GS99"
  );
  const coveragesGS30 = coverages.filter((cov: Coverage) => cov.code === "GS30");
  const coveragesGS09 = coverages.filter((cov: Coverage) => cov.code === "GS09");
  const coveragesGS99 = coverages.filter((cov: Coverage) => cov.code === "GS99");
  const GS09ValidValues = getValidCapitalValues(coveragesGS09[0]);
  const GS99ValidValues = getValidCapitalValues(coveragesGS99[0]);

  for (let index = 0; index < jsonData.length; index++) {
    const newInsured = jsonData[index];
    const rowNumber = index + 2; // Sumamos 2 porque el index es 0 y la primera línea el nombre de las columnas

    // Validación nombre de columnas
    if (index === 0) {
      let columnErrors = false;
      if (Object.keys(simulationColumnNames).length !== Object.keys(newInsured).length) {
        errors.push(`El número de columnas de la hoja de cálculo no coincide con el requerido.`);
        return { errors, newInsureds };
      }

      for (const key of Object.keys(newInsured)) {
        if (!(key in simulationColumnNames)) {
          errors.push(`La columna ${key} no coincide con las columnas requeridas.`);
          columnErrors = true;
        }
      }

      if (columnErrors) {
        return { errors, newInsureds };
      }
    }

    // Validación FECHA DE NACIMIENTO
    const birthDate = newInsured[simulationInsuredFields.birthDate];
    if (!birthDate) {
      errors.push(`La fecha de nacimiento es obligatoria (fila ${rowNumber})`);
    } else {
      const birthDateErrors = validateBirthDate(birthDate, coverages, rowNumber);
      if (birthDateErrors.length) {
        errors.push(...birthDateErrors);
      }
    }

    // Validación SEXO - no obligatorio en simulación
    const gender = newInsured[simulationInsuredFields.gender];
    if (gender && gender !== "M" && gender !== "H") {
      errors.push(`El género no puede ser diferente de 'H' o 'M' (fila ${rowNumber})`);
    }

    // Validación coberturas
    const GS30Value = newInsured[simulationInsuredFields.GS30] === "S";
    const GS09Value = `${newInsured[simulationInsuredFields.GS09]}`.trim();
    const GS99Value = `${newInsured[simulationInsuredFields.GS99]}`.trim();

    const isValidGS09Value = GS09ValidValues.includes(GS09Value);
    const isValidGS99Value = GS99ValidValues.includes(GS99Value);

    if (GS09Value && !isValidGS09Value) {
      errors.push(`Valor "${GS09Value}" inválido para la cobertura GS09 (fila ${rowNumber})`);
    }
    if (GS99Value && !isValidGS99Value) {
      errors.push(`Valor "${GS99Value}" inválido para la cobertura GS99 (fila ${rowNumber})`);
    }

    const GS30 = {
      ...coveragesGS30[0],
      checked: GS30Value,
    };
    const GS09 = {
      ...coveragesGS09[0],
      checked: !!GS09Value,
      capitalValue: GS09Value,
    };
    const GS99 = {
      ...coveragesGS99[0],
      checked: !!GS99Value,
      capitalValue: GS99Value,
    };

    // Añadimos el CÓDIGO POSTAL a la lista para su posterior validación
    const postalCode = `${newInsured[simulationInsuredFields.postalCode]}`;
    postalCodeList.add(postalCode);

    const newInsuredData = {
      birthDate,
      address: {
        postalCode,
      },
      gender: newInsured[simulationInsuredFields.gender],
      checked: true,
      coverages: [...mandatoryCoverages, GS30, GS09, GS99],
    };
    newInsureds.push(newInsuredData);
    birthDates.push(birthDate);
  }

  // Validación lista códigos postales únicos
  const resultados = await Promise.all(
    Array.from(postalCodeList).map((item) => validatePostalCode(item))
  );
  const postalCodeErrors = resultados.flat();
  if (postalCodeErrors) {
    errors.push(...postalCodeErrors);
  }

  if (errors.length) {
    return { errors, newInsureds: [] };
  }
  const uniqueBirthDates = new Set(birthDates);
  if (birthDates.length !== uniqueBirthDates.size) {
    warnings.push(
      "Se han encontrado posibles duplicados en el fichero (registros con la misma fecha de nacimiento). Por favor, revise los datos antes de continuar con la importación. ¿Desea continuar con la importación de todos modos?"
    );
  }

  return { errors, newInsureds, warnings };
};
